import { OrganizationBillingCategoryCode } from "@api/shared-runtime/enums";
import type { RouterInputs } from "@/trpc";
import { USAGE_BILLING_COLUMNS } from "./usage-billing-columns";

type GetUnitEventsInput = RouterInputs["adminUsageBilling"]["getUnitEvents"];

export type UsageEventSourceFilter = NonNullable<GetUnitEventsInput["source"]>;
export type UsageEventVoidStatusFilter = NonNullable<
  GetUnitEventsInput["voidStatus"]
>;
export type UsageEventSortDirection = NonNullable<
  GetUnitEventsInput["sortDirection"]
>;

export type UsageBillingEventFilters = {
  categoryCode: OrganizationBillingCategoryCode | undefined;
  source: UsageEventSourceFilter | undefined;
  voidStatus: UsageEventVoidStatusFilter;
  sortDirection: UsageEventSortDirection;
};

export const DEFAULT_EVENT_FILTERS: UsageBillingEventFilters = {
  categoryCode: undefined,
  source: undefined,
  voidStatus: "all",
  sortDirection: "desc",
};

// Category options mirror the summary table's fixed column order, so the
// filter reads consistently with the rest of the page.
export const CATEGORY_FILTER_OPTIONS: {
  value: OrganizationBillingCategoryCode;
  label: string;
}[] = USAGE_BILLING_COLUMNS.map((column) => ({
  value: column.categoryCode,
  label: column.columnLabel,
}));

// Provenance only: the per-emitter source always matches the category, so
// type-specific options would restate the category filter. The event detail
// sheet still shows each event's specific source.
export const SOURCE_FILTER_OPTIONS: {
  value: UsageEventSourceFilter;
  label: string;
}[] = [
  { value: "app", label: "App-recorded" },
  { value: "manual", label: "Manual" },
];

export const VOID_STATUS_FILTER_OPTIONS: {
  value: UsageEventVoidStatusFilter;
  label: string;
}[] = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "voided", label: "Voided" },
];

export const SORT_DIRECTION_OPTIONS: {
  value: UsageEventSortDirection;
  label: string;
}[] = [
  { value: "desc", label: "Newest first" },
  { value: "asc", label: "Oldest first" },
];

export function hasActiveEventFilters(
  filters: UsageBillingEventFilters,
): boolean {
  return (
    filters.categoryCode !== undefined ||
    filters.source !== undefined ||
    filters.voidStatus !== DEFAULT_EVENT_FILTERS.voidStatus ||
    filters.sortDirection !== DEFAULT_EVENT_FILTERS.sortDirection
  );
}
